import { cadenzaUrl } from '@disy/lib.backend/cadenza-url';
import { openUrl } from '@disy/lib/open-url';

import { hasPrivilege } from 'cadenza/access-management/model/object-privilege-util';
import { type RepositoryDto } from 'cadenza/api-client/dto/repository-dto';
import type { NavigatorTreeNodeDto } from 'cadenza/repository/model/navigator-tree-node-dto';
import type { ProjectId } from 'cadenza/repository/model/project-id.gen';
import type { RepositoryItemId } from 'cadenza/repository/model/repository-item-id';
import { repositoryItemIdEquals } from 'cadenza/repository/model/repository-item-id';
import { type RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { RepositoryTreeNodeDto } from 'cadenza/repository/model/repository-tree-node-dto';
import type { WorkbookName } from 'cadenza/repository/model/workbook-name.gen';
import { createResourceFilter } from 'cadenza/resource-filter/resource-filter';

import i18n from './repository-tree.properties';

export function createRepositorySelect(databaseRepositories: RepositoryDto[], selectedRepositoryName: RepositoryName) {
  return createResourceFilter(
    databaseRepositories.map((repository) => ({
      id: repository.name,
      text: repository.printName,
      selected: repository.name === selectedRepositoryName,
      resourceType: 'repository',
    })),
    'repository',
    'repository',
    { prependSelectAll: false },
  );
}

export function getSubtreeLabels(treeNode: RepositoryTreeNodeDto | NavigatorTreeNodeDto): string[] {
  return [
    ...(treeNode?.labels ?? []),
    ...(('properties' in treeNode && treeNode.properties?.labels) || []),
    ...('children' in treeNode ? (treeNode.children?.flatMap((childNode) => getSubtreeLabels(childNode)) ?? []) : []),
  ];
}

export function getRepositoryNamesPerFolder(
  treeNode: RepositoryTreeNodeDto,
  namesPerFolder: Record<string, string[]> = {},
): Record<string, string[]> {
  return (
    treeNode.children?.reduce((result, child) => {
      (result[treeNode.id] ??= []).push(child.label);
      return getRepositoryNamesPerFolder(child, result);
    }, namesPerFolder) ?? namesPerFolder
  );
}

export function getInitialTargetRepositoryName(
  databaseRepositories: RepositoryDto[],
  defaultRepositoryName?: RepositoryName,
) {
  // if there is only one (writeable) database repository, it must
  // be the target repository, otherwise start with the default
  if (databaseRepositories.length === 1) {
    return databaseRepositories[0].name;
  }
  // only return defaultRepositoryName if it is actually writable (and default is defined)
  if (databaseRepositories.find((repository) => repository.name === defaultRepositoryName)) {
    return defaultRepositoryName as RepositoryName;
  }
  return databaseRepositories[0]?.name;
}

export function getWritableTreeNodes(treeNodes: RepositoryTreeNodeDto[]): RepositoryTreeNodeDto[] {
  return treeNodes.reduce((fullModel: RepositoryTreeNodeDto[], currentModel) => {
    if (nodeOrAtLeastOneChildNodeIsWritable(currentModel)) {
      fullModel.push({ ...currentModel, children: getWritableTreeNodes(currentModel.children) });
    }
    return fullModel;
  }, []);
}

export function nodeIsWritable(treeNode: RepositoryTreeNodeDto): boolean {
  switch (treeNode.type) {
    case 'folder':
      return nodeHasWritePrivilege(treeNode);
    case 'root-folder':
      return nodeHasWritePrivilege(treeNode);
    default: // Was explicit defined for type 'Project'
      return nodeHasModifyContentPrivilege(treeNode);
  }
}

function nodeOrAtLeastOneChildNodeIsWritable(treeNode: RepositoryTreeNodeDto): boolean {
  return (
    nodeIsWritable(treeNode) ||
    ((treeNode.type === 'folder' || treeNode.type === 'root-folder') &&
      treeNode.children.some(nodeOrAtLeastOneChildNodeIsWritable))
  );
}

export function nodeHasWritePrivilege(treeNode: RepositoryTreeNodeDto) {
  return hasPrivilege(treeNode.currentUserPrivileges, 'write');
}

function nodeHasModifyContentPrivilege(treeNode: RepositoryTreeNodeDto) {
  return hasPrivilege(treeNode.currentUserPrivileges, 'modifyContent');
}

// This currently exists to preserve the fiction that `label` is always set,
// which is the case for everything but the root node.
// Ideally, components showing the root node had their own code to handle this case,
// as we cannot set the value on the server due to the actual value being language-dependent.
export function extendRootNode(treeNode: RepositoryTreeNodeDto): RepositoryTreeNodeDto {
  return {
    // @ts-expect-error – the root node does not have `label` set
    label: i18n('label.rootFolder'),
    ...treeNode,
  };
}

function getParentNode(treeNode: RepositoryTreeNodeDto, itemName: string): RepositoryTreeNodeDto | undefined {
  if (treeNode.children) {
    for (const node of treeNode.children) {
      if (node.itemName === itemName) {
        return treeNode;
      }
      if (node.children) {
        const found = getParentNode(node, itemName);
        if (found) {
          return found;
        }
      }
    }
  }
}

export function getFirstWritableFolderNode(treeNode: RepositoryTreeNodeDto): RepositoryTreeNodeDto | undefined {
  if (nodeHasWritePrivilege(treeNode)) {
    return treeNode;
  } else if (treeNode.children) {
    for (const node of treeNode.children) {
      const found = getFirstWritableFolderNode(node);
      if (found) {
        return found;
      }
    }
  }
}

export function getSelectedNode(treeNode: RepositoryTreeNodeDto, workbookName?: WorkbookName, projectId?: ProjectId) {
  if (projectId) {
    return getParentNode(treeNode, projectId.itemName)?.children.find((child) => child.itemName === projectId.itemName);
  }
  if (workbookName) {
    return getParentNode(treeNode, workbookName);
  }
}

export function navigateToTreeNodeUrl(treeNode: RepositoryTreeNodeDto) {
  if (treeNode.itemPath) {
    openUrl(cadenzaUrl(treeNode.itemPath));
  }
}

/**
 * Given a repository tree and item ID, this function performs a depth-first search for the node corresponding to the item ID.
 * If found, it returns an array of nodes representing the path from the root to (and including) the matching node.
 * If no tree node with the given item ID is found, it returns undefined.
 */
export function findPathToNodeInTree(
  treeRootNode: RepositoryTreeNodeDto,
  itemId: RepositoryItemId,
): RepositoryTreeNodeDto[] | undefined {
  if (repositoryItemIdEquals(treeRootNode.itemId, itemId)) {
    return [treeRootNode];
  }

  for (const child of treeRootNode.children) {
    const path = findPathToNodeInTree(child, itemId);
    if (path) {
      return [treeRootNode, ...path];
    }
  }
}
